import React, { useState, useMemo, useEffect } from 'react';
import { useUserDirectory, resolveUser, displayUser } from '../services/UserDirectoryService';
import { humanizeEntityName, pluralizeLabel } from '../utils/entityLabels';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  IconButton,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Popover,
  Autocomplete,
  TextField,
  Select,
  FormControl,
  InputLabel,
  LinearProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import PauseIcon from '@mui/icons-material/Pause';
import InventoryIcon from '@mui/icons-material/Inventory';
import EditIcon from '@mui/icons-material/Edit';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import MergeIcon from '@mui/icons-material/Merge';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsIcon from '@mui/icons-material/Settings';
import PushPinIcon from '@mui/icons-material/PushPin';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  getStatusColor,
  getHighlightColor,
  isActionEnabled,
  getDisabledReason,
  substituteTemplates,
} from '../utils/sampleResponses';
import { formatDateOnly, parseDateOnly } from '../utils/dateOnly';

// Icon mapping for actions
const getActionIcon = (iconName) => {
  const icons = {
    eye: <VisibilityIcon fontSize="small" />,
    'arrow-up': <ArrowUpwardIcon fontSize="small" />,
    pause: <PauseIcon fontSize="small" />,
    box: <InventoryIcon fontSize="small" />,
    edit: <EditIcon fontSize="small" />,
    scissors: <ContentCutIcon fontSize="small" />,
    merge: <MergeIcon fontSize="small" />,
    move: <OpenInNewIcon fontSize="small" />,
    download: <DownloadIcon fontSize="small" />,
    refresh: <RefreshIcon fontSize="small" />,
    plus: <AddIcon fontSize="small" />,
    clipboard: <AssignmentIcon fontSize="small" />,
    copy: <ContentCopyIcon fontSize="small" />,
    trash: <DeleteIcon fontSize="small" />,
    delete: <DeleteIcon fontSize="small" />,
  };
  return icons[iconName] || null;
};

// Column width mapping
const getColumnWidth = (width) => {
  const widths = {
    xs: 50,
    sm: 80,
    md: 120,
    lg: 180,
    xl: 250,
    auto: 'auto',
  };
  return widths[width] || widths.md;
};

// Get available operators based on column type
const getOperatorsForColumnType = (columnType) => {
  switch (columnType) {
    case 'NUMBER':
      return [
        { value: 'EQ', label: 'Equals' },
        { value: 'NEQ', label: 'Not equals' },
        { value: 'GT', label: 'Greater than' },
        { value: 'GTE', label: 'Greater than or equal' },
        { value: 'LT', label: 'Less than' },
        { value: 'LTE', label: 'Less than or equal' },
        { value: 'BETWEEN', label: 'Between' },
      ];
    case 'DATE':
    case 'DATETIME':
      return [
        { value: 'EQ', label: 'Equals' },
        { value: 'GT', label: 'After' },
        { value: 'GTE', label: 'On or after' },
        { value: 'LT', label: 'Before' },
        { value: 'LTE', label: 'On or before' },
        { value: 'BETWEEN', label: 'Between' },
      ];
    case 'STATUS':
      return [
        { value: 'EQ', label: 'Equals' },
        { value: 'NEQ', label: 'Not equals' },
        { value: 'IN', label: 'In list' },
        { value: 'NOT_IN', label: 'Not in list' },
      ];
    case 'TEXT':
    case 'LINK':
    default:
      return [
        { value: 'EQ', label: 'Equals' },
        { value: 'NEQ', label: 'Not equals' },
        { value: 'CONTAINS', label: 'Contains' },
        { value: 'STARTS_WITH', label: 'Starts with' },
        { value: 'IN', label: 'In list' },
      ];
  }
};

// Filter popover component for column filtering
const FilterPopover = ({ anchorEl, column, colors, onClose, onApply, rows }) => {
  const [operator, setOperator] = useState('EQ');
  const [value, setValue] = useState('');
  const [value2, setValue2] = useState(''); // For BETWEEN operator

  const operators = getOperatorsForColumnType(column?.type);
  // values already loaded for this column, offered as a pick list (typing still works)
  const valueOptions = useMemo(() => {
    if (!column || ['NUMBER', 'DATE', 'DATETIME'].includes(column.type)) return [];
    const seen = new Set();
    (rows || []).forEach((r) => {
      const v = (r.data || r)[column.columnId];
      if (v !== null && v !== undefined && v !== '' && typeof v !== 'object') seen.add(String(column.referenceEntity === 'User' ? displayUser(v) : v));
    });
    return seen.size <= 100 ? [...seen].sort() : [];
  }, [rows, column]);
  const open = Boolean(anchorEl);

  const handleApply = () => {
    if (!value.trim()) return;

    const filterValue = operator === 'BETWEEN' ? [value, value2] : value;
    const label = operator === 'BETWEEN'
      ? `${column.label}: ${value} - ${value2}`
      : `${column.label} ${operators.find(o => o.value === operator)?.label?.toLowerCase() || operator} ${value}`;

    onApply({
      columnId: column.columnId,
      operator,
      value: filterValue,
      label,
    });

    // Reset form
    setOperator('EQ');
    setValue('');
    setValue2('');
    onClose();
  };

  const handleClose = () => {
    setOperator('EQ');
    setValue('');
    setValue2('');
    onClose();
  };

  const getInputType = () => {
    switch (column?.type) {
      case 'NUMBER':
        return 'number';
      case 'DATE':
        return 'date';
      case 'DATETIME':
        return 'datetime-local';
      default:
        return 'text';
    }
  };

  if (!column) return null;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          bgcolor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 0,
          p: 2,
          minWidth: 280,
        },
      }}
    >
      <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 2 }}>
        Filter: {column.label}
      </Typography>

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel sx={{ color: colors.textSecondary }}>Operator</InputLabel>
        <Select
          value={operator}
          onChange={(e) => setOperator(e.target.value)}
          label="Operator"
          sx={{
            color: colors.textPrimary,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.border,
              borderRadius: 0,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#22c55e',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#22c55e',
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                bgcolor: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 0,
              },
            },
          }}
        >
          {operators.map((op) => (
            <MenuItem key={op.value} value={op.value} sx={{ color: colors.textPrimary }}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Status column: show dropdown for status values */}
      {column.type === 'STATUS' && column.statusMapping ? (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel sx={{ color: colors.textSecondary }}>Value</InputLabel>
          <Select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            label="Value"
            sx={{
              color: colors.textPrimary,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border,
                borderRadius: 0,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#22c55e',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#22c55e',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 0,
                },
              },
            }}
          >
            {Object.entries(column.statusMapping).map(([statusValue, statusConfig]) => (
              <MenuItem key={statusValue} value={statusValue} sx={{ color: colors.textPrimary }}>
                {statusConfig.label || statusValue}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <>
          {valueOptions.length > 0 && operator !== 'BETWEEN' ? (
            <Autocomplete
              freeSolo
              size="small"
              options={valueOptions}
              inputValue={value}
              onInputChange={(e, v) => setValue(v || '')}
              sx={{ mb: 2 }}
              renderInput={(params) => (
                <TextField {...params} label="Value" sx={{ '& .MuiOutlinedInput-root': { color: colors.textPrimary, '& fieldset': { borderColor: colors.border, borderRadius: 0 } }, '& .MuiInputLabel-root': { color: colors.textSecondary } }} />
              )}
            />
          ) : (
          <TextField
            fullWidth
            size="small"
            type={getInputType()}
            label={operator === 'BETWEEN' ? 'From' : 'Value'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={{
              mb: operator === 'BETWEEN' ? 1 : 2,
              '& .MuiOutlinedInput-root': {
                color: colors.textPrimary,
                '& fieldset': {
                  borderColor: colors.border,
                  borderRadius: 0,
                },
                '&:hover fieldset': {
                  borderColor: '#22c55e',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#22c55e',
                },
              },
              '& .MuiInputLabel-root': {
                color: colors.textSecondary,
              },
            }}
          />
          )}

          {operator === 'BETWEEN' && (
            <TextField
              fullWidth
              size="small"
              type={getInputType()}
              label="To"
              value={value2}
              onChange={(e) => setValue2(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  color: colors.textPrimary,
                  '& fieldset': {
                    borderColor: colors.border,
                    borderRadius: 0,
                  },
                  '&:hover fieldset': {
                    borderColor: '#22c55e',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#22c55e',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: colors.textSecondary,
                },
              }}
            />
          )}
        </>
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          size="small"
          onClick={handleClose}
          sx={{
            color: colors.textSecondary,
            borderRadius: 0,
          }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleApply}
          disabled={!value.trim() || (operator === 'BETWEEN' && !value2.trim())}
          sx={{
            bgcolor: '#22c55e',
            color: '#09090b',
            borderRadius: 0,
            '&:hover': {
              bgcolor: '#16a34a',
            },
            '&.Mui-disabled': {
              bgcolor: colors.border,
              color: colors.textMuted,
            },
          }}
        >
          Apply Filter
        </Button>
      </Box>
    </Popover>
  );
};

// Format cell value based on column type
const formatCellValue = (value, column) => {
  if (value === null || value === undefined) return '-';

  switch (column.type) {
    case 'NUMBER':
      if (column.format === 'currency') {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(value);
      }
      if (column.format === 'percent') {
        return `${value}%`;
      }
      return value.toLocaleString();

    case 'DATE':
      return formatDateOnly(value);

    case 'DATETIME':
      return new Date(value).toLocaleString();

    case 'CHIPS':
      // Plain-text fallback (e.g. CSV export) for array-valued fields -- the
      // real cell rendering is in CellContent below.
      return Array.isArray(value) ? value.join(', ') : String(value);

    default:
      // Handle JSONB PGobject {type: 'jsonb', value: '...'} and plain objects
      if (value && typeof value === 'object') {
        if (value.type === 'jsonb' && typeof value.value === 'string') return value.value;
        try { return JSON.stringify(value); } catch (e) { return String(value); }
      }
      return value;
  }
};

// A user id (ULID) shown as the friendly username, with the id as a tooltip. Anything that is not a known user id is shown as stored.
const UserCell = ({ value }) => {
  useUserDirectory(true);
  if (value === null || value === undefined || value === '') return '-';
  const name = resolveUser(value);
  return name ? <Tooltip title={String(value)} arrow><span>{name}</span></Tooltip> : String(value);
};

// Render cell content based on column type
const CellContent = ({ value, column, colors, onLinkClick, onOpenWorkspace, rowData }) => {
  if (column.referenceEntity === 'User') return <UserCell value={value} />;
  const formattedValue = formatCellValue(value, column);

  switch (column.type) {
    case 'STATUS':
      // no status yet (e.g. a suite never run): a dash like any empty cell, not an empty chip
      if (value === null || value === undefined || value === '') return '-';
      const statusColor = getStatusColor(value, column.statusMapping);
      const statusLabel = column.statusMapping?.[value]?.label || value;
      return (
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            bgcolor: `${statusColor}20`,
            color: statusColor,
            fontWeight: 600,
            borderRadius: 0,
            border: `1px solid ${statusColor}`,
            fontSize: '0.7rem',
          }}
        />
      );

    case 'LINK':
      // nothing to open (an order with no shipment yet): a dash like any empty cell, not a link
      if (value === null || value === undefined || value === '') return '-';
      return (
        <Typography
          variant="body2"
          sx={{
            color: '#22c55e',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
          onClick={() => {
            if (column.linkAction && onOpenWorkspace) {
              const la = column.linkAction;
              const navCtx = { viewId: la.view };
              if (la.searchField) {
                // valueField lets the link filter the target view by a
                // DIFFERENT field on this same row than the one clicked --
                // e.g. a location-count column linking to the Locations tab
                // filtered by this row's areaId, not by the count itself.
                const filterValue = la.valueField ? rowData?.[la.valueField] : value;
                navCtx.filters = { [la.searchField]: String(filterValue) };
              }
              onOpenWorkspace(la.workspace, la.workspace, false, navCtx);
            } else if (onLinkClick) {
              onLinkClick(column.columnId, value, rowData);
            }
          }}
        >
          {formattedValue}
        </Typography>
      );

    case 'BOOLEAN':
      const isTrue = value === true || value === 'true';
      return isTrue ? (
        <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e' }} />
      ) : (
        <CancelIcon sx={{ fontSize: 18, color: colors.textMuted, opacity: 0.4 }} />
      );

    case 'BADGE':
      return (
        <Chip
          label={formattedValue}
          size="small"
          sx={{
            bgcolor: colors.surfaceElevated,
            color: colors.textPrimary,
            fontWeight: 500,
            borderRadius: 0,
          }}
        />
      );

    case 'CHIPS': {
      // Array-valued fields (Area.pickedLevels, Area.selectedReplenishmentAreas,
      // Area.storageAttribute, etc.) -- was previously falling through to
      // 'default' and showing raw stringified JSON (FH-56, 2026-10-05).
      const items = Array.isArray(value) ? value : (value ? [value] : []);
      if (items.length === 0) {
        return <Typography variant="body2" sx={{ color: colors.textMuted }}>-</Typography>;
      }
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {items.map((v, i) => (
            <Chip
              key={`${v}-${i}`}
              label={String(v)}
              size="small"
              sx={{ bgcolor: colors.surfaceElevated, color: colors.textPrimary, borderRadius: 0, fontSize: '0.7rem' }}
            />
          ))}
        </Box>
      );
    }

    case 'PROGRESS_BAR': {
      const received = Number(value) || 0;
      const total = column.totalField ? Number(rowData?.[column.totalField]) || 0 : 0;

      if (!total) {
        return (
          <Typography variant="body2" sx={{ color: colors.textPrimary }}>
            {received.toLocaleString()} received
          </Typography>
        );
      }

      const percent = Math.max(0, Math.min(100, (received / total) * 100));
      return (
        <Box sx={{ minWidth: 90 }}>
          <LinearProgress
            variant="determinate"
            value={percent}
            sx={{
              height: 6,
              borderRadius: 0,
              bgcolor: colors.surfaceElevated,
              '& .MuiLinearProgress-bar': {
                bgcolor: percent >= 100 ? '#22c55e' : '#3b82f6',
              },
            }}
          />
          <Typography variant="caption" sx={{ color: colors.textMuted, mt: 0.5, display: 'block' }}>
            {received.toLocaleString()} / {total.toLocaleString()}
          </Typography>
        </Box>
      );
    }

    default:
      return (
        <Typography variant="body2" sx={{ color: colors.textPrimary }}>
          {formattedValue}
        </Typography>
      );
  }
};

// Row action menu component
const RowActionMenu = ({ row, rowActions, colors, onActionClick }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Filter actions to those available for this row
  const availableActions = rowActions.filter(
    (action) => row.actions && row.actions.includes(action.actionId)
  );

  if (availableActions.length === 0) return null;

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleActionClick = (action) => {
    handleClose();
    onActionClick(action, row);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{ color: colors.textSecondary }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 0,
          },
        }}
      >
        {availableActions.map((action) => (
          <MenuItem
            key={action.actionId}
            onClick={() => handleActionClick(action)}
            sx={{ color: colors.textPrimary }}
          >
            {action.icon && (
              <ListItemIcon sx={{ color: colors.textSecondary }}>
                {getActionIcon(action.icon)}
              </ListItemIcon>
            )}
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// Bulk actions bar component
const BulkActionsBar = ({ bulkActions, selectedCount, selectedIds, colors, onActionClick }) => {
  if (!bulkActions || bulkActions.length === 0 || selectedCount === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        bgcolor: 'rgba(34, 197, 94, 0.1)',
        borderBottom: `1px solid ${colors.border}`,
      }}
    >
      <Typography variant="body2" sx={{ color: colors.textPrimary, mr: 2 }}>
        {selectedCount} selected
      </Typography>
      {bulkActions.map((action) => {
        const enabled = isActionEnabled(action, selectedCount);
        const reason = getDisabledReason(action, selectedCount);

        return (
          <Tooltip key={action.actionId} title={reason || ''} arrow>
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={!enabled}
                startIcon={getActionIcon(action.icon)}
                onClick={() => onActionClick(action, null, selectedIds)}
                sx={{
                  borderColor: enabled ? '#22c55e' : colors.border,
                  color: enabled ? '#22c55e' : colors.textMuted,
                  borderRadius: 0,
                  '&:hover': {
                    borderColor: '#16a34a',
                    bgcolor: 'rgba(34, 197, 94, 0.08)',
                  },
                }}
              >
                {action.label}
              </Button>
            </span>
          </Tooltip>
        );
      })}
    </Box>
  );
};

// Global actions component
const GlobalActionsBar = ({ globalActions, colors, onActionClick }) => {
  // a header button with "items" opens a menu of actions instead of running one (FH-242)
  const [menu, setMenu] = useState({ anchor: null, items: [] });
  if (!globalActions || globalActions.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Menu
        anchorEl={menu.anchor}
        open={Boolean(menu.anchor)}
        onClose={() => setMenu({ anchor: null, items: [] })}
        PaperProps={{ sx: { bgcolor: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 0 } }}
      >
        {menu.items.map((item) => (
          <MenuItem
            key={item.actionId}
            onClick={() => { setMenu({ anchor: null, items: [] }); onActionClick(item); }}
            sx={{ color: colors.textPrimary, gap: 1 }}
          >
            {getActionIcon(item.icon)}
            {item.label}
          </MenuItem>
        ))}
      </Menu>
      {globalActions.map((action) => (
        <Button
          key={action.actionId}
          size="small"
          variant="outlined"
          startIcon={getActionIcon(action.icon)}
          onClick={(e) => (action.items && action.items.length > 0
            ? setMenu({ anchor: e.currentTarget, items: action.items })
            : onActionClick(action))}
          sx={{
            borderColor: colors.border,
            color: colors.textSecondary,
            borderRadius: 0,
            '&:hover': {
              borderColor: '#22c55e',
              color: '#22c55e',
              bgcolor: 'rgba(34, 197, 94, 0.08)',
            },
          }}
        >
          {action.label}
        </Button>
      ))}
    </Box>
  );
};

// Active filters bar component
const ActiveFiltersBar = ({ filters, colors, onFilterRemove }) => {
  if (!filters || filters.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        p: 1,
        bgcolor: colors.surface,
        borderBottom: `1px solid ${colors.border}`,
      }}
    >
      <FilterListIcon sx={{ color: colors.textSecondary, fontSize: '1rem', mr: 0.5 }} />
      <Typography variant="caption" sx={{ color: colors.textSecondary, mr: 1 }}>
        Filters:
      </Typography>
      {filters.map((filter) => (
        <Chip
          key={filter.filterId}
          label={filter.label}
          size="small"
          onDelete={filter.removable && onFilterRemove ? () => onFilterRemove(filter) : undefined}
          deleteIcon={filter.removable ? <CloseIcon sx={{ fontSize: '14px !important' }} /> : undefined}
          sx={{
            bgcolor: 'rgba(34, 197, 94, 0.1)',
            color: colors.textPrimary,
            borderRadius: 0,
            border: `1px solid ${filter.removable ? '#22c55e' : colors.border}`,
            fontSize: '0.75rem',
            '& .MuiChip-deleteIcon': {
              color: colors.textSecondary,
              '&:hover': {
                color: '#ef4444',
              },
            },
          }}
        />
      ))}
    </Box>
  );
};

// Main TableRenderer component
// A table in the plain shape (columns with "field", rows as flat maps) is turned into the rendered shape (columnId,
// rows as { data }), so a table saved that way is shown instead of crashing the page (FH-368).
const normalizeTableShape = (table) => {
  if (!table || typeof table !== 'object') return table;
  const columns = Array.isArray(table.columns)
    ? table.columns.filter((c) => c && typeof c === 'object').map((c) => (c.columnId || !c.field ? c : { ...c, columnId: c.field }))
    : table.columns;
  const rows = Array.isArray(table.rows)
    ? table.rows.filter((r) => r && typeof r === 'object').map((r) => (r.data && typeof r.data === 'object' ? r : { data: r }))
    : table.rows;
  return columns === table.columns && rows === table.rows ? table : { ...table, columns, rows };
};

const TableRenderer = ({
  table,
  colors,
  onRowAction,
  onBulkAction,
  onGlobalAction,
  onLinkClick,
  onRowSelect,
  onOpenWorkspace,
  onConfigureTable,
  onPinToBoard,
  onFilterRemove,
  onAddFilter,
  sourceQuery,
  fillHeight = false,
}) => {
  // eslint-disable-next-line no-param-reassign
  table = normalizeTableShape(table);
  // Readable row noun for the counters ("picked inventories", not "pickedinventorys").
  const rowNoun = (count) => {
    const label = (table.rowNoun || humanizeEntityName(table.entityType || 'item')).toLowerCase();
    return count === 1 ? label : pluralizeLabel(label);
  };
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: null,
    row: null,
    selectedIds: [],
  });

  // Row click handler — selects row and triggers link column click for child grid loading
  const handleRowClick = (event, row) => {
    // Ignore clicks on checkboxes, buttons, links, or action menus
    const tag = event.target.tagName;
    if (tag === 'INPUT' || tag === 'BUTTON' || tag === 'SVG' || tag === 'PATH' ||
        event.target.closest('button') || event.target.closest('a') ||
        event.target.closest('[role="menuitem"]')) return;

    // Find the first LINK column to simulate link click behavior
    const linkColumn = columns.find(c => c.type === 'LINK');
    if (linkColumn && onLinkClick) {
      onLinkClick(linkColumn.columnId, row.data[linkColumn.columnId], row.data);
    } else if (onRowSelect) {
      // no link column: the click still picks the row for the panels below it
      onRowSelect(row.data);
    }
  };

  // Client-side sort state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSortClick = (column) => {
    if (!column.sortable) return;
    const direction = sortColumn === column.columnId && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(column.columnId);
    setSortDirection(direction);
    setPage(0);
    // only part of the list is loaded: sorting those rows alone would be wrong, so ask the server (FH-204)
    if (pagingPartial && table.paging.sort) table.paging.sort(column.columnId, direction);
  };

  // Client-side filter state
  const [activeFilters, setActiveFilters] = useState([]);

  // Filter popover state
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterColumn, setFilterColumn] = useState(null);

  const handleFilterClick = (event, column) => {
    setFilterAnchorEl(event.currentTarget);
    setFilterColumn(column);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setFilterColumn(null);
  };

  const handleFilterApply = (filterData) => {
    const newFilter = {
      ...filterData,
      filterId: `${filterData.columnId}-${Date.now()}`,
      removable: true,
    };
    setActiveFilters(prev => [...prev, newFilter]);
  };

  const handleFilterRemove = (filter) => {
    setActiveFilters(prev => prev.filter(f => f.filterId !== filter.filterId));
  };

  // Pagination state
  const [page, setPage] = useState(0);
  // A grid may set its own starting page size (view JSON "pageSize"), e.g. a short
  // fixed list an admin should see in one page; otherwise 10.
  const [rowsPerPage, setRowsPerPage] = useState(table?.pageSize || 10);

  const { columns = [], rows = [], rowActions, bulkActions, globalActions, selectable } = table || {};

  // Server paging (FH-204): a grid that loads its list a batch at a time passes
  // table.paging = { total, loading, loadMore(upTo), sort(columnId, direction) }. The count shows
  // the real total, and moving past the loaded rows loads the next batch.
  const paging = table?.paging;
  const pagingPartial = !!(paging && paging.total != null && paging.total > rows.length);

  // user-id columns are shown as usernames, so filter and sort by what the person sees
  const userColumnIds = useMemo(() => new Set(columns.filter(c => c.referenceEntity === 'User').map(c => c.columnId)), [columns]);
  const directoryVersion = useUserDirectory(userColumnIds.size > 0);
  const shown = (row, columnId) => (userColumnIds.has(columnId) ? displayUser(row.data[columnId]) : row.data[columnId]);

  // Client-side filtering
  const filteredRows = useMemo(() => {
    if (!activeFilters.length) return rows;
    return rows.filter(row => {
      return activeFilters.every(filter => {
        const value = String(shown(row, filter.columnId) ?? '').toLowerCase();
        const filterVal = String(filter.value).toLowerCase();
        switch (filter.operator) {
          case 'CONTAINS': return value.includes(filterVal);
          case 'STARTS_WITH': return value.startsWith(filterVal);
          case 'EQ': return value === filterVal;
          case 'NEQ': return value !== filterVal;
          default: return true;
        }
      });
    });
  }, [rows, activeFilters, userColumnIds, directoryVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Client-side sorting
  const sortedRows = useMemo(() => {
    if (!sortColumn) return filteredRows;
    const col = columns.find(c => c.columnId === sortColumn);
    if (!col) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      let valA = shown(a, sortColumn);
      let valB = shown(b, sortColumn);
      // Handle nulls
      if (valA == null && valB == null) return 0;
      if (valA == null) return sortDirection === 'asc' ? 1 : -1;
      if (valB == null) return sortDirection === 'asc' ? -1 : 1;
      // Sort by type
      if (col.type === 'NUMBER') {
        valA = parseFloat(valA) || 0;
        valB = parseFloat(valB) || 0;
      } else if (col.type === 'DATE') {
        valA = parseDateOnly(valA)?.getTime() || 0;
        valB = parseDateOnly(valB)?.getTime() || 0;
      } else if (col.type === 'DATETIME') {
        valA = new Date(valA).getTime() || 0;
        valB = new Date(valB).getTime() || 0;
      } else {
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRows, sortColumn, sortDirection, columns, userColumnIds, directoryVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate paginated rows from sorted data
  const paginatedRows = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Load the next batch when the page asked for goes past the rows loaded so far
  const ensureLoaded = (upTo) => {
    if (pagingPartial && paging.loadMore && rows.length < upTo) paging.loadMore(upTo);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    ensureLoaded((newPage + 1) * rowsPerPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    const size = parseInt(event.target.value, 10);
    setRowsPerPage(size);
    setPage(0); // Reset to first page when changing rows per page
    ensureLoaded(size);
  };

  // the list was reloaded shorter (a new search or filter): back to the first page
  const pagedCount = !activeFilters.length && paging?.total != null ? Math.max(paging.total, rows.length) : filteredRows.length;
  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= pagedCount) setPage(0);
  }, [pagedCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle row selection
  const handleSelectRow = (rowId) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowId)) {
      newSelected.delete(rowId);
    } else {
      newSelected.add(rowId);
    }
    setSelectedRows(newSelected);
  };

  // Handle select all (selects all rows on current page)
  const handleSelectAll = () => {
    const selectableOnPage = paginatedRows.filter((r) => r.selectable !== false);
    const allOnPageSelected = selectableOnPage.every((r) => selectedRows.has(r.rowId));

    if (allOnPageSelected) {
      // Deselect all on current page
      const newSelected = new Set(selectedRows);
      selectableOnPage.forEach((r) => newSelected.delete(r.rowId));
      setSelectedRows(newSelected);
    } else {
      // Select all on current page
      const newSelected = new Set(selectedRows);
      selectableOnPage.forEach((r) => newSelected.add(r.rowId));
      setSelectedRows(newSelected);
    }
  };

  // Check if all rows on current page are selected
  const selectableOnPage = paginatedRows.filter((r) => r.selectable !== false);
  const allOnPageSelected = selectableOnPage.length > 0 && selectableOnPage.every((r) => selectedRows.has(r.rowId));
  const someOnPageSelected = selectableOnPage.some((r) => selectedRows.has(r.rowId));

  // Handle action execution (with confirmation support)
  const executeAction = (action, row, selectedIds = []) => {
    if (action.confirmRequired) {
      // Substitute variables in confirm message
      const confirmMessage = substituteTemplates(
        action.confirmMessage || `Are you sure you want to ${action.label.toLowerCase()}?`,
        row?.data,
        selectedIds
      );
      setConfirmDialog({
        open: true,
        action,
        row,
        selectedIds,
        message: confirmMessage,
      });
    } else {
      performAction(action, row, selectedIds);
    }
  };

  // Perform the actual action
  const performAction = (action, row, selectedIds = []) => {
    const sentence = substituteTemplates(action.sentence, row?.data, selectedIds);

    if (row) {
      // Row action
      onRowAction && onRowAction(action, row, sentence);
    } else if (selectedIds.length > 0) {
      // Bulk action
      onBulkAction && onBulkAction(action, selectedIds, sentence);
    } else {
      // Global action
      onGlobalAction && onGlobalAction(action, sentence);
    }
  };

  // Handle dialog confirmation
  const handleConfirmAction = () => {
    const { action, row, selectedIds } = confirmDialog;
    performAction(action, row, selectedIds);
    setConfirmDialog({ open: false, action: null, row: null, selectedIds: [] });
  };

  // Get selected row IDs as array
  const selectedIdsArray = Array.from(selectedRows);

  return (
    <Box sx={{
      mt: fillHeight ? 0 : 2,
      ...(fillHeight ? { flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 } : {}),
      // Self-constrain so wide tables don't push the parent (chat bubble) wider
      // than its allotted space. The TableContainer inside has overflow:auto
      // which provides horizontal scrolling for the wide table.
      maxWidth: '100%',
      minWidth: 0,
      overflow: 'hidden',
    }}>
      {/* Table header with title and global actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          p: 1,
          bgcolor: colors.surface,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ color: colors.textPrimary, fontWeight: 600 }}
          >
            {table.title}
          </Typography>
          {table.description && (
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              {table.description}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GlobalActionsBar
            globalActions={globalActions}
            colors={colors}
            onActionClick={(action) => executeAction(action)}
          />
          {/* Pin to Dashboard button */}
          {onPinToBoard && (
            <Tooltip title="Pin to Dashboard" arrow>
              <IconButton
                size="small"
                onClick={() => onPinToBoard('TABLE', { table }, sourceQuery)}
                sx={{
                  color: colors.textSecondary,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 0,
                  p: 0.75,
                  '&:hover': {
                    color: '#22c55e',
                    borderColor: '#22c55e',
                    bgcolor: 'rgba(34, 197, 94, 0.08)',
                  },
                }}
              >
                <PushPinIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* Table configuration gear icon */}
          <Tooltip title="Configure table columns" arrow>
            <IconButton
              size="small"
              onClick={() => onConfigureTable && onConfigureTable(table.tableId, table.entityType)}
              sx={{
                color: colors.textSecondary,
                border: `1px solid ${colors.border}`,
                borderRadius: 0,
                p: 0.75,
                '&:hover': {
                  color: '#22c55e',
                  borderColor: '#22c55e',
                  bgcolor: 'rgba(34, 197, 94, 0.08)',
                },
              }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Active filters bar */}
      <ActiveFiltersBar
        filters={activeFilters}
        colors={colors}
        onFilterRemove={handleFilterRemove}
      />

      {/* Filter result count */}
      {activeFilters.length > 0 && (
        <Box sx={{ px: 1, py: 0.5, bgcolor: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
          <Typography variant="caption" sx={{ color: colors.textSecondary }}>
            Showing {sortedRows.length} of {rows.length} {rowNoun(rows.length)}
          </Typography>
        </Box>
      )}

      {/* Bulk actions bar */}
      <BulkActionsBar
        bulkActions={bulkActions}
        selectedCount={selectedRows.size}
        selectedIds={selectedIdsArray}
        colors={colors}
        onActionClick={(action, row, ids) => executeAction(action, row, ids)}
      />

      {/* Data table */}
      <TableContainer
        sx={{
          bgcolor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderTop: selectedRows.size > 0 ? 'none' : `1px solid ${colors.border}`,
          ...(fillHeight ? { flex: '1 1 auto', minHeight: 0 } : { maxHeight: 440 }), // ~10 rows unless filling the pane
          overflow: 'auto',
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: colors.surfaceElevated }}>
              {selectable && (
                <TableCell
                  padding="checkbox"
                  sx={{
                    borderBottom: `1px solid ${colors.border}`,
                    bgcolor: colors.surfaceElevated,
                  }}
                >
                  <Checkbox
                    indeterminate={someOnPageSelected && !allOnPageSelected}
                    checked={allOnPageSelected}
                    onChange={handleSelectAll}
                    sx={{
                      color: colors.textMuted,
                      '&.Mui-checked': { color: '#22c55e' },
                      '&.MuiCheckbox-indeterminate': { color: '#22c55e' },
                    }}
                  />
                </TableCell>
              )}
              {rowActions && rowActions.length > 0 && (
                <TableCell
                  sx={{
                    width: 50,
                    borderBottom: `1px solid ${colors.border}`,
                    bgcolor: colors.surfaceElevated,
                  }}
                />
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.columnId}
                  sx={{
                    color: colors.textSecondary,
                    fontWeight: 600,
                    borderBottom: `1px solid ${colors.border}`,
                    bgcolor: colors.surfaceElevated,
                    width: getColumnWidth(column.width),
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    letterSpacing: '0.05em',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      onClick={() => column.sortable && handleSortClick(column)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.25,
                        cursor: column.sortable ? 'pointer' : 'default',
                        userSelect: 'none',
                        '&:hover': column.sortable ? { color: colors.textPrimary } : {},
                      }}
                    >
                      {column.label}
                      {column.sortable && sortColumn === column.columnId && (
                        <Box component="span" sx={{ fontSize: '0.7rem', ml: 0.25 }}>
                          {sortDirection === 'asc' ? '▲' : '▼'}
                        </Box>
                      )}
                      {column.sortable && sortColumn !== column.columnId && (
                        <Box component="span" sx={{ fontSize: '0.6rem', ml: 0.25, opacity: 0.3 }}>
                          ▲
                        </Box>
                      )}
                    </Box>
                    {column.filterable && (
                      <Tooltip title={`Filter by ${column.label}`} arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleFilterClick(e, column); }}
                          sx={{
                            p: 0.25,
                            color: colors.textMuted,
                            opacity: 0.6,
                            '&:hover': {
                              opacity: 1,
                              color: '#22c55e',
                              bgcolor: 'transparent',
                            },
                          }}
                        >
                          <FilterListIcon sx={{ fontSize: '0.875rem' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length === 0 && paging?.loading && (
              <TableRow>
                <TableCell colSpan={columns.length + 2} sx={{ textAlign: 'center', color: colors.textMuted, py: 3, borderBottom: 'none' }}>
                  Loading more…
                </TableCell>
              </TableRow>
            )}
            {/* Group header row renderer */}
            {table.groupBy && (() => {
              // Group rows by the groupBy field
              const groupField = table.groupBy;
              const qtyField = table.groupQuantityField || 'quantity';
              const qtyLabel = table.groupQuantityLabel || 'Qty';
              const groups = {};
              const ungrouped = [];
              paginatedRows.forEach(row => {
                const groupVal = row.data[groupField];
                if (groupVal) {
                  if (!groups[groupVal]) groups[groupVal] = [];
                  groups[groupVal].push(row);
                } else {
                  ungrouped.push(row);
                }
              });

              const renderDataRow = (row, indent = 0) => {
                const isSelected = selectedRows.has(row.rowId);
                return (
                  <TableRow
                    key={row.rowId}
                    hover
                    selected={isSelected}
                    onClick={(e) => handleRowClick(e, row)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: colors.hoverBg },
                      '&.Mui-selected': { bgcolor: 'rgba(34, 197, 94, 0.08)' },
                    }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox" sx={{ borderBottom: `1px solid ${colors.border}` }}>
                        <Checkbox checked={isSelected} disabled={row.selectable === false}
                          onChange={() => handleSelectRow(row.rowId)}
                          sx={{ color: colors.textMuted, '&.Mui-checked': { color: '#22c55e' } }} />
                      </TableCell>
                    )}
                    {rowActions && rowActions.length > 0 && (
                      <TableCell sx={{ borderBottom: `1px solid ${colors.border}`, py: 0.75 }}>
                        <RowActionMenu row={row} rowActions={rowActions} colors={colors} onActionClick={(action, r) => executeAction(action, r)} />
                      </TableCell>
                    )}
                    {columns.map((column, colIdx) => (
                      <TableCell key={column.columnId} sx={{ borderBottom: `1px solid ${colors.border}`, py: 0.75, pl: colIdx === 0 ? 2 + indent * 3 : undefined }}>
                        {column.columnId === groupField ? null : (
                          <CellContent value={row.data[column.columnId]} column={column} colors={colors} rowData={row.data} onLinkClick={onLinkClick} onOpenWorkspace={onOpenWorkspace} />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              };

              return (
                <>
                  {Object.entries(groups).map(([groupVal, groupRows]) => {
                    const isExpanded = expandedGroups.has(groupVal);
                    const itemCount = groupRows.length;
                    const totalQty = groupRows.reduce((sum, r) => sum + (Number(r.data[qtyField]) || 0), 0);
                    // Use first row's data for group-level actions and selection
                    const groupRepRow = groupRows[0];
                    const allChildIds = groupRows.map(r => r.rowId);
                    const allChildrenSelected = allChildIds.every(id => selectedRows.has(id));
                    const someChildrenSelected = allChildIds.some(id => selectedRows.has(id));

                    const handleGroupSelect = (e) => {
                      e.stopPropagation();
                      setSelectedRows(prev => {
                        const next = new Set(prev);
                        if (allChildrenSelected) {
                          allChildIds.forEach(id => next.delete(id));
                        } else {
                          allChildIds.forEach(id => next.add(id));
                        }
                        return next;
                      });
                    };

                    return (
                      <React.Fragment key={`grp-${groupVal}`}>
                        <TableRow
                          hover
                          selected={allChildrenSelected}
                          onClick={(e) => {
                            if (e.target.closest('button') || e.target.closest('input')) return;
                            setExpandedGroups(prev => {
                              const next = new Set(prev);
                              if (next.has(groupVal)) next.delete(groupVal);
                              else next.add(groupVal);
                              return next;
                            });
                          }}
                          sx={{
                            cursor: 'pointer', bgcolor: colors.surfaceElevated,
                            '&:hover': { bgcolor: colors.hoverBg },
                            '&.Mui-selected': { bgcolor: 'rgba(34, 197, 94, 0.08)' },
                          }}
                        >
                          {selectable && (
                            <TableCell padding="checkbox" sx={{ borderBottom: `1px solid ${colors.border}` }}>
                              <Checkbox
                                checked={allChildrenSelected}
                                indeterminate={someChildrenSelected && !allChildrenSelected}
                                onChange={handleGroupSelect}
                                sx={{ color: colors.textMuted, '&.Mui-checked': { color: '#22c55e' } }}
                              />
                            </TableCell>
                          )}
                          {rowActions && rowActions.length > 0 && (
                            <TableCell sx={{ borderBottom: `1px solid ${colors.border}`, py: 0.75 }}>
                              <RowActionMenu row={groupRepRow} rowActions={rowActions} colors={colors}
                                onActionClick={(action, r) => executeAction(action, r)} />
                            </TableCell>
                          )}
                          <TableCell
                            colSpan={columns.length}
                            sx={{ borderBottom: `1px solid ${colors.border}`, py: 0.75, fontWeight: 700 }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {isExpanded ? <ExpandMoreIcon fontSize="small" sx={{ color: '#22c55e' }} /> : <ChevronRightIcon fontSize="small" sx={{ color: colors.textSecondary }} />}
                              <Typography variant="body2" sx={{ fontWeight: 700, color: colors.textPrimary }}>
                                {groupVal}
                              </Typography>
                              <Chip label={`${itemCount} item${itemCount !== 1 ? 's' : ''}`} size="small"
                                sx={{ ml: 1, bgcolor: 'rgba(34, 197, 94, 0.1)', color: colors.textSecondary, borderRadius: 0, fontSize: '0.7rem', height: 20 }} />
                              <Chip label={`${qtyLabel}: ${totalQty}`} size="small"
                                sx={{ bgcolor: 'rgba(34, 197, 94, 0.1)', color: colors.textSecondary, borderRadius: 0, fontSize: '0.7rem', height: 20 }} />
                              {groupRows[0]?.data?.locationId && (
                                <Typography variant="caption" sx={{ color: colors.textMuted, ml: 1 }}>
                                  @ {groupRows[0].data.locationId}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                        {isExpanded && groupRows.map(row => renderDataRow(row, 1))}
                      </React.Fragment>
                    );
                  })}
                  {ungrouped.length > 0 && Object.keys(groups).length > 0 && (
                    <TableRow>
                      <TableCell colSpan={columns.length + (selectable ? 1 : 0) + (rowActions?.length > 0 ? 1 : 0)}
                        sx={{ borderBottom: `1px solid ${colors.border}`, py: 0.5, bgcolor: colors.surfaceElevated }}>
                        <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600 }}>Unpalletized</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {ungrouped.map(row => renderDataRow(row, 0))}
                </>
              );
            })()}

            {/* Standard flat row rendering (when no groupBy) */}
            {!table.groupBy && paginatedRows.map((row) => {
              const isSelected = selectedRows.has(row.rowId);
              const highlightBg = row.highlighted
                ? getHighlightColor(row.highlightColor)
                : 'transparent';

              return (
                <TableRow
                  key={row.rowId}
                  hover
                  selected={isSelected}
                  onClick={(e) => handleRowClick(e, row)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: highlightBg,
                    '&:hover': {
                      bgcolor: isSelected
                        ? 'rgba(34, 197, 94, 0.16)'
                        : colors.hoverBg,
                    },
                    '&.Mui-selected': {
                      bgcolor: 'rgba(34, 197, 94, 0.08)',
                    },
                  }}
                >
                  {selectable && (
                    <TableCell
                      padding="checkbox"
                      sx={{ borderBottom: `1px solid ${colors.border}` }}
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={row.selectable === false}
                        onChange={() => handleSelectRow(row.rowId)}
                        sx={{
                          color: colors.textMuted,
                          '&.Mui-checked': { color: '#22c55e' },
                        }}
                      />
                    </TableCell>
                  )}
                  {rowActions && rowActions.length > 0 && (
                    <TableCell
                      sx={{
                        borderBottom: `1px solid ${colors.border}`,
                        py: 1,
                      }}
                    >
                      <RowActionMenu
                        row={row}
                        rowActions={rowActions}
                        colors={colors}
                        onActionClick={(action, r) => executeAction(action, r)}
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell
                      key={column.columnId}
                      sx={{
                        borderBottom: `1px solid ${colors.border}`,
                        py: 1,
                      }}
                    >
                      <CellContent
                        value={row.data[column.columnId]}
                        column={column}
                        colors={colors}
                        rowData={row.data}
                        onLinkClick={onLinkClick}
                        onOpenWorkspace={onOpenWorkspace}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={pagedCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{
          bgcolor: colors.surface,
          borderTop: `1px solid ${colors.border}`,
          color: colors.textSecondary,
          // Without this, this Box has no flex properties of its own and defaults to
          // flex-shrink: 1 inside the fillHeight flex column above (TableContainer is
          // the only sibling marked flex: '1 1 auto') -- the pagination bar's own
          // content (rows-per-page select, page nav icons) was getting compressed
          // below its natural height instead of TableContainer simply taking exactly
          // the remaining space, showing as a half-covered footer with its own
          // scrollbars. Changing pages re-triggers the same squeeze on every reflow
          // since the table body's height changes with the row count. Found live
          // 2026-10-06 ("the bottom section... is half covered and now has scroll
          // bars... if I change the pages it does that too").
          flexShrink: 0,
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            color: colors.textMuted,
            fontSize: '0.75rem',
          },
          '& .MuiTablePagination-select': {
            color: colors.textPrimary,
          },
          '& .MuiTablePagination-selectIcon': {
            color: colors.textSecondary,
          },
          '& .MuiIconButton-root': {
            color: colors.textSecondary,
            '&.Mui-disabled': {
              color: colors.textMuted,
            },
          },
        }}
        labelRowsPerPage="Rows per page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count} ${rowNoun(count)}`}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null, row: null, selectedIds: [] })}
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary }}>
          Confirm Action
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: colors.textSecondary }}>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, action: null, row: null, selectedIds: [] })}
            sx={{ color: colors.textSecondary, borderRadius: 0 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            sx={{
              bgcolor: confirmDialog.action?.confirmStyle === 'danger' ? '#ef4444' : '#22c55e',
              color: '#09090b',
              borderRadius: 0,
              '&:hover': {
                bgcolor: confirmDialog.action?.confirmStyle === 'danger' ? '#dc2626' : '#16a34a',
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Popover */}
      <FilterPopover
        anchorEl={filterAnchorEl}
        column={filterColumn}
        colors={colors}
        rows={table.rows}
        onClose={handleFilterClose}
        onApply={handleFilterApply}
      />

    </Box>
  );
};

export default TableRenderer;
